/**
 * Shared source of truth for the property Setup workflow.
 *
 * Both PropertyChecklist (the snapshot strip) and SetupTab (the full
 * thirteen-step page) compute their state from this single function so
 * the snapshot and the tab can never disagree.
 */

import type { EnrichedProperty, PropertyTab } from './types';

export type SetupStepKey =
  | 'customer'
  | 'manager'
  | 'ambassador'
  | 'rooms'
  | 'service'
  | 'cmb_certificate'
  | 'charter_issued'
  | 'certified_since'
  | 'verify_page'
  | 'suite_invite'
  | 'charter_delivered'
  | 'listed'
  | 'agreement';

export interface SetupStep {
  key: SetupStepKey;
  /** Long form, e.g. "1. Customer linked". */
  label: string;
  /** Short form for the snapshot pill, e.g. "Customer". */
  shortLabel: string;
  /** Operator-facing description of what this step means. */
  description: string;
  /** True when the step's underlying state is satisfied. */
  done: boolean;
  /** True for steps that don't block the property going live. */
  optional: boolean;
  /** True for steps that aren't actionable yet (placeholders). */
  disabled: boolean;
  /** Tab to jump to when the operator wants to action this step. */
  tab: PropertyTab | null;
  /** Who drives the tick — informational only, doesn't affect rendering. */
  driver: 'operator' | 'system';
  /** Soft warning shown inline on the Setup tab when something needs attention. */
  warning?: string;
  /** When set, this step's action cannot be taken yet because an earlier
   *  step is still open. Short operator-facing requirement naming what to
   *  do first, e.g. "Log the first service". The board shows it as a calm
   *  lock note and disables the action button. It does not stop the step
   *  being computed or the card being shown. */
  blockedBy?: string;
}

export interface SetupContext {
  /** Whether `hosp_properties.agreement_pdf_url` is populated, OR an
   *  auto-confirmed property_agreements row exists from an accepted invoice. */
  agreementOnFile: boolean;
  /** Whether at least one active `cert_certificates` row exists for the property. */
  hasActiveCertificate: boolean;
  /** Whether the property has Charter Suite access — either a
   *  charter_suite_invites row exists for the property (pending),
   *  OR a hosp_property_managers row exists (accepted at property),
   *  OR a hosp_group_managers row exists for the property's group
   *  (accepted at group level). */
  hasSuiteAccess: boolean;
  /** Whether `hosp_properties.ambassador_decided` is true. Lets the
   *  step close when the operator has explicitly picked "No ambassador"
   *  rather than left the slot untouched. */
  ambassadorDecided: boolean;
}

export function computeSetupSteps(property: EnrichedProperty, ctx: SetupContext): SetupStep[] {
  const assuredCurrent = !!property.assured_until && new Date(property.assured_until).getTime() >= Date.now();

  // Dependency chain for the board's prerequisite notes. The first service
  // log is the activation event: the Service Certificate and the assurance
  // dates both derive from it. The assurance dates in turn gate the Suite
  // invite, the Charter delivery and the directory listing. A step that
  // can't be actioned until its prerequisite is met carries a `blockedBy`.
  const serviceDone = !!property.last_service_date;
  const assuranceConfirmed = !!property.certified_since && !!property.assured_until;
  // A property certified at least once is a renewal, not a new property:
  // its messaging must not read as first-time setup, and its assurance is
  // flagged only once it has actually expired, never while still current.
  const everCertified = !!property.certified_since;
  const assuranceExpired = everCertified && !assuredCurrent;
  // A property needs a (re)service when it has never been serviced, or when
  // it is a renewal whose assurance has lapsed. In both cases the Service Log
  // step is the actionable root and the Service Certificate waits on it, so
  // the board never prompts for a certificate while no service log exists.
  const needsService = !serviceDone || assuranceExpired;
  const SERVICE_FIRST = 'Log the first service';
  const RENEWAL_DUE = 'Assurance expired, log a renewal service';
  const SERVICE_MSG = everCertified ? RENEWAL_DUE : SERVICE_FIRST;
  const ASSURANCE_FIRST = 'Confirm the assurance dates';

  // Build warnings, calm and plain, one line each.
  const certifiedSinceMissing = !property.certified_since && !!property.last_service_date;
  const assuredUntilMissing = !property.assured_until && !!property.last_service_date;
  const listedWithoutAssurance = !!property.is_published && !assuredCurrent;

  return [
    {
      key: 'customer',
      label: '1. Customer created & linked',
      shortLabel: 'Customer',
      description: 'The legal customer this property bills to. Hard requirement to activate the property: status cannot move to Active without one.',
      done: !!property.customer_id,
      optional: false,
      disabled: false,
      tab: 'overview',
      driver: 'operator',
    },
    {
      key: 'manager',
      label: '2. Manager assigned',
      shortLabel: 'Manager',
      description: 'The CMB-side manager who coordinates service for this property. Their email is the internal channel for escalations.',
      done: !!property.assigned_manager_id,
      optional: false,
      disabled: false,
      tab: 'overview',
      driver: 'operator',
    },
    {
      key: 'ambassador',
      label: '3. Ambassador assigned',
      shortLabel: 'Ambassador',
      description: 'Who the property is attributed to for commission. Pick the introducer or pick "No ambassador" explicitly. A silent null at invoice creation forfeits commission permanently with no backfill.',
      done: !!property.ambassador_id || ctx.ambassadorDecided,
      optional: false,
      disabled: false,
      tab: 'overview',
      driver: 'operator',
    },
    {
      key: 'service',
      label: '4. Service Log created & linked',
      shortLabel: 'Service log',
      description: 'At least one Peace of Sleep service log linked to this property. The first one is the activation event: it triggers the certificate, the assurance dates, and the Charter number all at once. A renewal whose assurance has lapsed reopens this step until a fresh service is logged.',
      done: !needsService,
      optional: false,
      disabled: false,
      tab: 'records',
      driver: 'operator',
    },
    {
      key: 'cmb_certificate',
      label: '5. Service Certificate created & linked',
      shortLabel: 'Service certificate',
      description: 'A clinical service certificate issued from a completed service log. Multi-stage workflow: the operator drafts, submits for verification, an authoriser approves, and only then does the certificate become valid. Not a blocker, but the audit trail is not complete until it is certified.',
      done: ctx.hasActiveCertificate,
      optional: false,
      disabled: false,
      tab: 'records',
      driver: 'operator',
      blockedBy: needsService ? SERVICE_MSG : undefined,
    },
    {
      key: 'rooms',
      label: '6. Rooms breakdown',
      shortLabel: 'Rooms',
      description: 'Audit-tidy step. A default "Totals" row covers the property at first, but splitting the matrix into per-room rows (so each cert can attribute to a real bedroom) is the polish that completes the audit trail. Does not block anything upstream.',
      done: (property.total_rooms ?? 0) > 0,
      optional: false,
      disabled: false,
      tab: 'rooms',
      driver: 'operator',
    },
    {
      key: 'charter_issued',
      label: '7. Charter number created',
      shortLabel: 'Charter number',
      description: 'The property\'s permanent identifier (POS-XX-NNNNN). Auto-stamped at property creation by the issue_charter_on_active trigger. Used by the Verify page, the QR code, and the bedside certificate. Operators do not generate or edit this.',
      done: !!property.charter_number,
      optional: false,
      disabled: false,
      tab: 'overview',
      driver: 'system',
    },
    {
      key: 'certified_since',
      label: '8. Certified Since / Assured Until',
      shortLabel: 'Assurance dates',
      description: 'Certified Since is when the assurance period began (usually the first service date). Assured Until is when it expires (Certified Since plus the tier interval). Both auto-derive from the first service date.',
      done: assuredCurrent,
      optional: false,
      disabled: false,
      tab: 'overview',
      driver: 'system',
      blockedBy: serviceDone ? undefined : SERVICE_MSG,
      warning: assuranceExpired
        ? RENEWAL_DUE
        : certifiedSinceMissing
          ? 'Service on file but Certified Since is missing.'
          : assuredUntilMissing
            ? 'Service on file but Assured Until is missing.'
            : undefined,
    },
    {
      key: 'verify_page',
      label: '9. Visible on Verify page',
      shortLabel: 'Verify page',
      description: 'The public Verify page at peaceofsleep.vip/{country}/verify/{charter}/ goes live automatically once the property has a Charter number AND is Active. No action required.',
      done: !!property.charter_number && property.status === 'active',
      optional: false,
      disabled: false,
      tab: 'overview',
      driver: 'system',
    },
    {
      key: 'suite_invite',
      label: '10. Charter Suite invite sent',
      shortLabel: 'Suite invite',
      description: 'Send the manager an invite to their Charter Suite workspace. Once accepted they can manage their own listing, download their certificate, and see their service history.',
      done: ctx.hasSuiteAccess,
      optional: false,
      disabled: false,
      tab: 'overview',
      driver: 'operator',
      blockedBy: assuranceConfirmed ? undefined : ASSURANCE_FIRST,
    },
    {
      key: 'charter_delivered',
      label: '11. Charter delivered',
      shortLabel: 'Charter delivered',
      description: 'The framed Charter has been physically posted to the property. Tick this when the parcel goes out, with the courier reference for the audit trail.',
      done: !!property.charter_delivered_at,
      optional: false,
      disabled: false,
      tab: 'overview',
      driver: 'operator',
      blockedBy: assuranceConfirmed ? undefined : ASSURANCE_FIRST,
    },
    {
      key: 'listed',
      label: '12. Listed on directory',
      shortLabel: 'Listed',
      description: 'Property is published on the public directory at peaceofsleep.vip/{country}/. Requires Assured Until to be current.',
      done: property.is_published === true && assuredCurrent,
      optional: false,
      disabled: false,
      tab: 'property',
      driver: 'operator',
      blockedBy: assuredCurrent ? undefined : (everCertified ? RENEWAL_DUE : ASSURANCE_FIRST),
      warning: listedWithoutAssurance
        ? (everCertified
            ? 'Listed but assurance has expired, re-service to renew.'
            : 'Listed but Assured Until is missing or in the past.')
        : assuredUntilMissing
          ? 'Assured Until is missing. Sort the assurance dates before listing.'
          : undefined,
    },
    {
      key: 'agreement',
      label: '13. Agreement signed',
      shortLabel: 'Agreement',
      description: 'Signed Participation Agreement on file. Stamps automatically when the customer accepts an invoice carrying the POS T&Cs. Manual PDF upload also possible via the Records tab.',
      done: ctx.agreementOnFile,
      optional: false,
      disabled: false,
      tab: 'records',
      driver: 'system',
    },
  ];
}
